import { useCallback, useEffect, useRef, useState } from "react";

interface AsyncState<T> {
  data: T | null;
  error: string | null;
  /** HTTP status of the failure when the error came from the API, else null. */
  errorStatus: number | null;
  loading: boolean;
}

/** Runs `load` on mount and whenever `deps` change. A slower, older response never overwrites a newer one. */
export function useAsync<T>(load: () => Promise<T>, deps: readonly unknown[]) {
  const [state, setState] = useState<AsyncState<T>>({ data: null, error: null, errorStatus: null, loading: true });
  const [nonce, setNonce] = useState(0);
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    let current = true;
    setState((s) => ({ ...s, error: null, errorStatus: null, loading: true }));
    loadRef.current().then(
      (data) => current && setState({ data, error: null, errorStatus: null, loading: false }),
      (err: unknown) =>
        current &&
        setState((s) => ({
          ...s,
          error: err instanceof Error ? err.message : "Terjadi kesalahan",
          errorStatus: typeof (err as { statusCode?: unknown })?.statusCode === "number" ? (err as { statusCode: number }).statusCode : null,
          loading: false,
        })),
    );
    return () => {
      current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { ...state, reload };
}
